import { useDispatch, useSelector } from "react-redux";

import {
  API_SA_BASE_URL,
  API_ENDPOINTS,
  API_BASE_URL,
  API_CHINA_SA_BASE_URL,
} from "../constants/api";
import { ACR_KEY, ACR_VALUES, CLIENT_ID } from "../constants/appConstants";
import {
  USER_DATA_NAMESPACE,
  USER_SESSION_EXPIRED_TIME,
  USER_SESSION_ID,
} from "../constants/storage";
import { LocalStorage } from "../services/storage";
import { actionLogout } from "../store/actions/user-actions";
import { SignInEntity, SignOutEntity, ConfirmPassEntity } from "../types/auth";
import { generateToken } from "../utils/UtilsTokenGenerator";
import { REQUEST_TYPE } from "../types/user";
import { UtilsRequest } from "../utils/UtilsRequest";
import config from "../constants/serverConfig";
import { ROUTES } from "../configs/routes";
import { ApplicationState } from "../store";
import { LayoutState } from "../store/reducers/layout-reducer";
import triggerGAEvent from "../utils/UtilsTriggerGAEvent";
import {
  EVENT_CATEGORY_HEADER,
  EVENT_HEADER_ACTION_LOGIN,
  EVENT_HEADER_ACTION_LOGIN_CHINA,
  EVENT_HEADER_ACTION_LOGOUT,
} from "../constants/analytics";
import { ServiceEntity } from "../types/services";
import { UserState } from "../store/reducers/user-reducer";
import { GEOIP_CHINA_COUNTRY_CODE } from "../constants/locale";

export const useAuth = () => {
  const dispatch = useDispatch();
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const userState = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  const login = (redirectURL?: string) => {
    const appState = generateToken(32);
    const SA_BASE_URL =
      userState.userCountryCode === GEOIP_CHINA_COUNTRY_CODE
        ? API_CHINA_SA_BASE_URL
        : API_SA_BASE_URL;
    const EVENT_HEADER_LOGIN =
      userState.userCountryCode === GEOIP_CHINA_COUNTRY_CODE
        ? EVENT_HEADER_ACTION_LOGIN_CHINA
        : EVENT_HEADER_ACTION_LOGIN;

    const authUrl = `${API_BASE_URL}${API_ENDPOINTS.AUTH_SIGNIN_URL}?returnUrl=${API_BASE_URL}${redirectURL}?success=1&appId=${CLIENT_ID}`;

    LocalStorage.remove(USER_SESSION_EXPIRED_TIME);

    const paramsEntity: SignInEntity = {
      response_type: "code",
      client_id: CLIENT_ID,
      locale: layoutState.languageCode,
      countryCode: layoutState.countryCode,
      redirect_uri: authUrl,
      state: appState,
      goBackURL: API_BASE_URL,
      acr_values: ACR_VALUES,
      acr_key: ACR_KEY,
    };

    const params = new URLSearchParams({ ...paramsEntity });

    triggerGAEvent(EVENT_CATEGORY_HEADER, EVENT_HEADER_LOGIN);
    window.location.replace(`${SA_BASE_URL}${API_ENDPOINTS.LOGIN}?${params}`);
  };

  const logout = async () => {
    const appState = generateToken(32);
    const authUrl = `${API_BASE_URL}${API_ENDPOINTS.AUTH_SIGNOUT_URL}?returnUrl=${API_BASE_URL}/`;

    LocalStorage.remove(USER_SESSION_ID);

    const paramsEntity: SignOutEntity = {
      client_id: CLIENT_ID,
      signOutURL: authUrl,
      state: appState,
      acr_values: ACR_VALUES,
    };

    dispatch(actionLogout());
    await LocalStorage.remove(USER_DATA_NAMESPACE);
    LocalStorage.remove(USER_SESSION_EXPIRED_TIME);

    const params = new URLSearchParams({ ...paramsEntity });

    triggerGAEvent(EVENT_CATEGORY_HEADER, EVENT_HEADER_ACTION_LOGOUT);
    window.location.replace(
      `${API_SA_BASE_URL}${API_ENDPOINTS.LOGOUT}?${params}`
    );
  };

  const confirmPassword = (
    services: Array<ServiceEntity>,
    requestType: REQUEST_TYPE
  ) => {
    const arrServices: String[] = [];

    services.forEach((service) => {
      arrServices.push(service.service_code);
    });

    UtilsRequest.setRequestInfo(arrServices, requestType);
    const appState = generateToken(32);

    const redirectConf = `${API_BASE_URL}${ROUTES.MYDATA_HISTORY}`;
    const goBackUrl = `${config.APP_BASE_URL_NO_PARAMS}${ROUTES.MYDATA}`;
    const paramsEntity: ConfirmPassEntity = {
      locale: layoutState.languageCode,
      countryCode: layoutState.countryCode,
      redirect_uri: redirectConf,
      state: appState,
      goBackURL: goBackUrl,
    };
    //update and make dynamic

    const params = new URLSearchParams({ ...paramsEntity });

    window.location.replace(
      `${API_SA_BASE_URL}${API_ENDPOINTS.CONFIRM_PASS_URL}?${params}`
    );
  };

  return { login, logout, confirmPassword };
};
